import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { of, zip, from, Subscription, Observable } from 'rxjs'
import { toArray, mergeMap, map, finalize, switchMap, filter, take } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { ApplicationProviderService } from '../../services/application.service'
import { ApplicationRecoveryPointDto } from 'src/app/services/dtos'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'

interface ApplicationRecoveryPointRow {
  rp: ApplicationRecoveryPointDto
  applicationName: string
  timestampString: string
}

@Component({
  selector: 'app-account-retention-policy',
  templateUrl: './account-retention-policy.component.html',
  styleUrls: ['./account-retention-policy.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountRetentionPolicyComponent implements OnInit, OnDestroy {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private apiService: ApiService,
    public uiService: UiService,
    public accountService: AccountService,
    private applicationService: ApplicationProviderService,
    private accessLevelService: AccessLevelService,
    private router: Router
  ) {}

  @ViewChild('releaseRpsDialog', { static: true }) releaseRpsDialog: ModalDialogComponent

  protected retainedRps: Array<ApplicationRecoveryPointRow> = null
  protected selectedRps: Array<ApplicationRecoveryPointRow> = []
  private settingsSubscription: Subscription = null
  protected retainedRpsSubscription: Subscription = null
  private updateSubscription: Subscription = null
  private submitted = false
  private unprotecting = false

  // Permission properties
  canManageRetentionPolicy$: Observable<boolean>
  canManageRetentionPolicy = false

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  retentionSettingsFormModel = this.formBuilder.group({
    recentRetained: [
      '',
      [Validators.required, Validators.pattern('^[0-9]+$'), Validators.min(1), Validators.max(1000)],
    ],
    daysRetained: ['', [Validators.required, Validators.pattern('^[0-9]+$'), Validators.min(0), Validators.max(1000)]],
    weeksRetained: ['', [Validators.required, Validators.pattern('^[0-9]+$'), Validators.min(0), Validators.max(1000)]],
    monthsRetained: [
      '',
      [Validators.required, Validators.pattern('^[0-9]+$'), Validators.min(0), Validators.max(1000)],
    ],
    quartersRetained: [
      '',
      [Validators.required, Validators.pattern('^[0-9]+$'), Validators.min(0), Validators.max(1000)],
    ],
  })

  get loadingSettings() {
    return this.settingsSubscription != null
  }

  get loadingRetainedRps() {
    return this.retainedRps == null
  }

  get updateButtonDisabled() {
    return !(this.retentionSettingsFormModel.dirty && this.retentionSettingsFormModel.valid)
  }

  get releaseButtonText() {
    return this.selectedRps.length > 0 ? `Release (${this.selectedRps.length})` : 'Release'
  }

  get submitting() {
    return subscriptionActive(this.updateSubscription)
  }

  get doneSubmitting() {
    return this.submitted && !this.retentionSettingsFormModel.dirty
  }

  get globalFilterFields() {
    return ['applicationName', 'timestampString']
  }

  get unprotectDisabled(): boolean {
    return this.unprotecting || this.selectedRps.length === 0
  }

  get protectRecoveryPointEnabled(): boolean {
    return this.accountService.protectRecoveryPointEnabled
  }

  get showRetainedRps() {
    return this.protectRecoveryPointEnabled && this.retainedRps?.length > 0
  }

  ngOnInit() {
    // Initialize permissions - retention policy requires UPDATE_ACCOUNT permission
    this.canManageRetentionPolicy$ = this.accessLevelService.hasAccountPermission(AccountPermission.UPDATE_ACCOUNT)

    // Set up permission-based form control state
    this.canManageRetentionPolicy$.subscribe((canManage) => {
      this.canManageRetentionPolicy = canManage
      if (this.canManageRetentionPolicy) {
        this.retentionSettingsFormModel.enable()
      } else {
        this.retentionSettingsFormModel.disable()
      }
    })

    // load the subscription settings
    this.loadRetentionSettings()
    this.loadRetainedRps()
  }

  ngOnDestroy() {
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe()
      this.settingsSubscription = null
    }
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe()
      this.updateSubscription = null
    }
    if (this.retainedRpsSubscription) {
      this.retainedRpsSubscription.unsubscribe()
      this.retainedRpsSubscription = null
    }
  }

  loadRetentionSettings() {
    this.settingsSubscription = this.accountService.accountIdStream
      .pipe(
        switchMap((_accountId) =>
          this.accountService.retentionSettings$.pipe(finalize(() => (this.settingsSubscription = null)))
        )
      )
      .subscribe(
        ([recent, days, weeks, months, quarters]) => {
          this.retentionSettingsFormModel.setValue({
            recentRetained: recent,
            daysRetained: days,
            weeksRetained: weeks,
            monthsRetained: months,
            quartersRetained: quarters,
          })
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  loadRetainedRps() {
    // Wait for the account to finish loading before fetching data; on a hard refresh ngOnInit runs
    // before AccountShellComponent.loadAccount$ resolves, so reading accountService.accountId
    // synchronously yields null and the request goes out with an empty account path segment.
    this.retainedRpsSubscription = this.accountService.accountIdStream
      .pipe(
        filter((accountId) => !!accountId),
        take(1),
        switchMap((accountId) =>
          this.apiService.listRecoveryPoints$(accountId, true).pipe(
            mergeMap((rps) => from(rps)),
            mergeMap((rp) => zip(of(rp), this.applicationService.loadApplication$(rp.applicationId, accountId, false))),
            map(([rp, app]) => {
              return {
                rp: rp,
                applicationName: app.name,
                timestampString: this.rpTimestampString(rp),
              } as ApplicationRecoveryPointRow
            }),
            toArray()
          )
        ),
        finalize(() => (this.retainedRpsSubscription = null))
      )
      .subscribe(
        (rps) => (this.retainedRps = rps),
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  updateRetentionSettings() {
    this.updateSubscription = this.accountService
      .setRetentionSettings$(
        this.retentionSettingsFormModel.value.recentRetained,
        this.retentionSettingsFormModel.value.daysRetained,
        this.retentionSettingsFormModel.value.weeksRetained,
        this.retentionSettingsFormModel.value.monthsRetained,
        this.retentionSettingsFormModel.value.quartersRetained
      )
      .pipe(finalize(() => (this.updateSubscription = null)))
      .subscribe(
        () => {
          this.submitted = true
          this.retentionSettingsFormModel.markAsPristine()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  rpTimestampString(rp: ApplicationRecoveryPointDto) {
    return rp.timestamp.toLocaleString([], {
      timeZoneName: 'short',
      minute: 'numeric',
      hour: 'numeric',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
  }

  releaseRpsClick() {
    this.releaseRpsDialog.open()
  }

  dismissReleaseModal() {
    this.releaseRpsDialog.close()
  }

  unprotectRps() {
    this.unprotecting = true
    from(this.selectedRps)
      .pipe(
        mergeMap((rpr) =>
          this.apiService.updateApplicationRecoveryPoint(
            this.accountService.accountId,
            rpr.rp.applicationId,
            rpr.rp.recoveryPointId,
            false
          )
        ),
        finalize(() => {
          this.unprotecting = false
          this.selectedRps = []
          this.dismissReleaseModal()
        })
      )
      .subscribe(
        () => {
          this.loadRetainedRps()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }
}
